import {
  deleteAsset,
  deleteAssetsByProject,
  getAsset,
  getAssetKeysByProject,
  putAsset,
} from "./db";

/**
 * Module-scoped cache mapping `assetKey` to an active `blob:` object URL.
 * Lifecycles bound to the page session — entries are created on first lookup
 * or download and only released when the underlying asset is invalidated
 * (e.g. unpin in PR #3). This lets `useOfflineImageUrl` resolve cached
 * assets synchronously at mount time, avoiding a fallback→blob `src` swap
 * that would reset Radix Avatar / `<img loading>` state.
 */
const objectUrlCache = new Map<string, string>();
/** Tracks keys we've already probed in IDB so we don't re-issue an async read. */
const probedKeys = new Set<string>();

/**
 * In-flight `ensureAssetCached` downloads keyed by asset key, so concurrent
 * callers for one key (pin, resync, the backfill subscriber) share a single
 * download instead of each fetching the same bytes.
 */
const inFlightDownloads = new Map<string, Promise<Blob>>();

export function getCachedBlobUrl(key: string): string | null {
  return objectUrlCache.get(key) ?? null;
}

/**
 * Whether `key`'s blob is already in the in-memory cache — i.e. an
 * `OfflineImage` for it would resolve synchronously on its first render, with
 * no async lookup. Lets a caller skip a load placeholder / fade when the
 * image is already in hand. See [[offline-asset-cache]].
 */
export function hasCachedAsset(key: string): boolean {
  return objectUrlCache.has(key);
}

export async function getBlobUrl(key: string): Promise<string | null> {
  const cached = objectUrlCache.get(key);
  if (cached) return cached;
  if (probedKeys.has(key)) return null;
  const asset = await getAsset(key);
  probedKeys.add(key);
  if (!asset) return null;
  const url = URL.createObjectURL(asset.blob);
  objectUrlCache.set(key, url);
  return url;
}

function releaseCachedUrl(key: string): void {
  const cached = objectUrlCache.get(key);
  if (cached) {
    URL.revokeObjectURL(cached);
    objectUrlCache.delete(key);
  }
  probedKeys.delete(key);
}

/**
 * Warm the in-memory object-URL cache for `key` directly from an in-hand blob
 * — synchronously, with no IndexedDB round-trip. Used to seed a just-synced
 * record's thumbnail from its local capture bytes so the grid tile resolves
 * the image on its first render (see [[offline-asset-cache]] § Provisional
 * seed). Mirrors `downloadAndStore`'s cache tail: any prior URL for the key is
 * revoked first, so re-priming never leaks.
 */
export function primeObjectUrl(key: string, blob: Blob): void {
  releaseCachedUrl(key);
  objectUrlCache.set(key, URL.createObjectURL(blob));
  probedKeys.add(key);
}

export async function downloadAndStore(
  key: string,
  url: string,
  projectId: string,
): Promise<Blob> {
  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok) {
    throw new Error(
      `Failed to download asset ${key}: HTTP ${response.status}`,
    );
  }
  const blob = await response.blob();
  await putAsset({
    key,
    blob,
    contentType: blob.type || "application/octet-stream",
    fetchedAt: Date.now(),
    projectId,
  });
  // Release any prior URL for this key (e.g. previous revision) before
  // adopting the new blob.
  releaseCachedUrl(key);
  objectUrlCache.set(key, URL.createObjectURL(blob));
  probedKeys.add(key);
  return blob;
}

/**
 * The single chokepoint for getting an asset into the cache. Concurrent calls
 * for the same key share one download — pin, resync and the backfill
 * subscriber all route through here, so an asset is fetched at most once at a
 * time. A durably-cached blob is returned as-is; a *provisional* blob (a local
 * seed awaiting its server thumbnail) counts as missing, so it is re-downloaded
 * and upgraded. See [[offline-asset-cache]].
 */
export async function ensureAssetCached(
  key: string,
  url: string,
  projectId: string,
): Promise<Blob> {
  const inFlight = inFlightDownloads.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<Blob> => {
    const existing = await getAsset(key);
    if (existing && !existing.provisional) return existing.blob;
    return downloadAndStore(key, url, projectId);
  })();

  inFlightDownloads.set(key, promise);
  try {
    return await promise;
  } finally {
    inFlightDownloads.delete(key);
  }
}

/** Remove a single asset from IDB and revoke its cached object URL. */
export async function invalidateCachedAsset(key: string): Promise<void> {
  releaseCachedUrl(key);
  await deleteAsset(key);
}

/**
 * Remove every asset for a project (called by PR #3's unpin). Awaits the IDB
 * delete *before* releasing object URLs so a concurrent `getAsset` from a
 * re-pin can't see the row right after the URL cache is wiped. The brief
 * window where IDB is empty but object URLs are still live is harmless —
 * `blob:` URLs reference the in-memory Blob, not the IDB row.
 */
export async function invalidateCachedAssetsByProject(
  projectId: string,
): Promise<number> {
  const keys = await getAssetKeysByProject(projectId);
  const deleted = await deleteAssetsByProject(projectId);
  for (const key of keys) releaseCachedUrl(key);
  return deleted;
}

export function resetBlobCacheForTesting(): void {
  for (const url of objectUrlCache.values()) URL.revokeObjectURL(url);
  objectUrlCache.clear();
  probedKeys.clear();
  inFlightDownloads.clear();
}

/**
 * Re-exported from its new home so offline callers keep their import path.
 * See `@/lib/semaphore`.
 */
export { createSemaphore } from "@/lib/semaphore";
